/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, put, select, takeEvery,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';

import getSearchQAS from '../../../../../../apis/qas-address-search-apis/getSearchQAS';
import getFormatQAS from '../../../../../../apis/qas-address-search-apis/getFormatQAS';

import { getBrandConfig } from '../../App/App-Selectors';
import {
    clearFormatQas, clearAddressQas, flagOffShowValidatedQAS, flagOnShowValidatedQAS, getFormatQas, loadFormatQas, loadAddressQas,
    resetQasState,
} from './AddressQAS-Actions';
import { getCountryCodeFromTwoToThreeDigit } from '../../../../Checkout/helpers/countryCode';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../Checkout/ducks/Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { getOrderId } from '../../Cart/Cart-Selectors';
import { flagOnQasFailed } from '../../../../Checkout/ducks/Common/ducks/AddressSearchQAS/AddressSearchQAS-Actions';

const DEFAULT_QAS_SEARCH_LIMIT = 7;
const countryISOAlfa3Code = {
    US: 'USA',
    CA: 'CAN',
};

function* workerQueryQasAddressSearch(args) {
    let response = {};
    const { searchText, countryCode, searchTextAddressLine } = args.data;
    const isoCountryCode = countryCode?.length !== 3 ? countryISOAlfa3Code[countryCode] : countryCode;
    const brandProperties = yield select(getBrandConfig);
    const queryLimit = brandProperties['qas-address-search-limit'] || DEFAULT_QAS_SEARCH_LIMIT;

    const payload = {
        country_iso: isoCountryCode,
        max_suggestions: Number(queryLimit),
        components: {
            unspecified: [searchText],
        },
        datasets: [`${isoCountryCode.toLowerCase().substring(0, 2)}-address`],
    };

    const responseQAS = yield call(getSearchQAS, payload);
    response = responseQAS.data?.result || {};

    if (response) {
        if (!response.suggestions || response.suggestions?.length === 0) {
            payload.components.unspecified[0] = searchTextAddressLine;
            const resQueryQasAddressLine = yield call(getSearchQAS, payload);
            response = resQueryQasAddressLine.data?.result || {};
            if (resQueryQasAddressLine.data?.result?.suggestions) {
                yield put(loadAddressQas(resQueryQasAddressLine.data?.result));
            }
        } else {
            yield put(loadAddressQas(response));
        }
    } else if (responseQAS.data?.error) {
        yield put(clearAddressQas(responseQAS?.data?.error?.detail));
    }

    return response;
}

function* workerGetFormatQAS(args) {
    let response = {};
    const { address } = args.data;

    if (address && address.format) {
        const payload = {
            global_address_key: address.global_address_key,
        };

        const resFormatQas = yield call(getFormatQAS, payload);
        response = resFormatQas;
        if (resFormatQas?.data?.result) {
            yield put(loadFormatQas(resFormatQas?.data?.result));
        } else if (resFormatQas.data?.error) {
            yield put(clearFormatQas());
        }
    }

    return response;
}

function* watcherAddressQASGetFormat() {
    yield takeEvery(getFormatQas().type, workerGetFormatQAS);
}

function alignQasFormatResponse(formattedAddress) {
    const { components, address } = formattedAddress;

    let address1 = address.address_line_1;
    let address2 = `${address.address_line_2} ${address.address_line_3}`;

    if (['APO', 'FPO'].includes(components.locality?.town.name)) {
        address1 = components.street.full_name;
        address2 = components.secondary_delivery_service.full_name;
    }

    const returnAddress = {
        addressLine1: address1,
        addressLine2: address2,
        city: address.locality,
        state: address.region,
        country: components.country_iso_3,
        zipcode: components.postal_code.full_name,
    };

    return returnAddress;
}

function trimAndLowerCase(value) {
    return (value) ? value.trim().toLocaleLowerCase() : '';
}

function trimStr(value) {
    return (value) ? value.trim() : '';
}

const removeSpaceAndComma = (str) => str.replace(/ |,/gi, '');

const removeComma = (str) => str.replace(/,/gi, '');

const formAddressFromMatchedStr = (address, arrUserInputedAddress) => {
    let addressFormation = '';
    const subStringCollection = [];
    for (let t = address.matched || [], r = address.text, n = t.length - 1; n >= 0; n -= 1) {
        const extractedSubString = r.substring(t[n][0], t[n][1]);
        const extract = extractedSubString.split(' ');
        for (let i = 0; i < extract.length; i += 1) {
            subStringCollection.push(extract[i]);
        }
        addressFormation = `${addressFormation}${extractedSubString}`;
    }
    const suggestionText = removeComma(address.text).split(' ');
    const addressFormationStr = removeSpaceAndComma(addressFormation);
    const addresslineUserInputed = removeSpaceAndComma(address.text);

    if (addressFormationStr?.toLocaleLowerCase() === addresslineUserInputed?.toLocaleLowerCase()) {
        return true;
    }
    const notMatched = [];
    for (let i = 0; i < suggestionText.length; i += 1) {
        if (suggestionText[i] !== subStringCollection[i]) {
            notMatched.push(suggestionText[i]);
        }
    }

    const userInputedText = arrUserInputedAddress.toLocaleLowerCase().split(' ');
    if (notMatched.length > 0) {
        // eslint-disable-next-line arrow-body-style
        const notMatchedCheck = notMatched.map((notMatchedString) => {
            return userInputedText.includes(notMatchedString.toLocaleLowerCase()) ? 'Y' : 'N';
        });
        if (notMatchedCheck.indexOf('N') >= 0) {
            return false;
        }
        return true;
    }
    return false;
};

/**
 * @description this is the function that will check the QAS address, format and get the config file,  which will be use to make the dynamic regex that will be used to check the suggestion address from the QAS Api
 * @param {*} addressMatchedArray
 * @param {*} lineAddress
 * @returns sendUpdatedObject: Object
 */
function checkAddressMatchingPercentage(addressMatchedArray, lineAddress) {
    const matched = formAddressFromMatchedStr(addressMatchedArray, lineAddress);
    return matched;
}
/**
 * @description this will check if the children array is an entire subset of the parent array
 * @param {Array} parent
 * @param {Array} children
 * @returns {boolean}
 */
function checkSubset(parent, children) {
    let matchedAddressLength = 0;
    children.forEach((indexObject) => {
        if (parent.includes(indexObject)) {
            matchedAddressLength += 1;
        }
    });
    const matchedAddressOnLength = ((children.length - 1) === matchedAddressLength);
    const matchedAddressPercentage = (parseInt(((matchedAddressLength / children.length) * 100), 10) > 60);
    return !!(matchedAddressOnLength || matchedAddressPercentage);
}
/**
 * @description this will split the string on the base of the separater that has been provided
 * @param {*} arraySet
 * @param {string} [separator=' ']
 * @returns {boolean}
 */
function convertToArray(arraySet, separator = ' ') {
    return trimAndLowerCase(arraySet).split(separator);
}

/**
 * @description it will verify the address that will be if(QASAddress is equal to the LineAddress) || if(QASAddress is equal to the (Updated address {only if possible on Line Address}))
 * @param {String} parentAddress
 * @param {String} childAddress
 * @param {string} [updatedAddress='']
 * @returns {Boolean}
 */
function verfiytheAddress(parentAddress, childAddress, updatedAddress = '') {
    const parentAddressArray = convertToArray(parentAddress);
    const childAddressArray = convertToArray(childAddress);
    const checkAddress = checkSubset(parentAddressArray, childAddressArray);
    let checkUpdateAddress = false;
    if (!checkAddress && updatedAddress !== '') {
        const updatedAddressArray = convertToArray(updatedAddress);
        checkUpdateAddress = checkSubset(parentAddressArray, updatedAddressArray);
    }
    return (checkAddress || checkUpdateAddress);
}

function compareAddressWithQASFormat(qasFormatAddress, recipientFormAddress) {
    const resultSet = {
        allowSave: false,
        recipientFormAddress,
    };

    let checkZipcode = false;
    const checkCity = (trimAndLowerCase(qasFormatAddress.city) === trimAndLowerCase(recipientFormAddress.city));
    const checkState = trimAndLowerCase(qasFormatAddress.state) === trimAndLowerCase(recipientFormAddress.state);

    const countryQAS = qasFormatAddress.country && qasFormatAddress.country.length === 2
        ? getCountryCodeFromTwoToThreeDigit(qasFormatAddress.country)
        : qasFormatAddress.country;
    const checkCountry = trimAndLowerCase(countryQAS) === trimAndLowerCase(recipientFormAddress.country);
    if (/(-)/g.test(String(qasFormatAddress.zipcode))) {
        const qasFormatAddressZipCode = convertToArray(qasFormatAddress.zipcode, '-');
        if (checkSubset(qasFormatAddressZipCode, [recipientFormAddress.zipCode])) {
            resultSet.recipientFormAddress.zipCode = qasFormatAddress.zipcode;
            checkZipcode = true;
        }
    } else if (trimAndLowerCase(qasFormatAddress.zipcode) === trimAndLowerCase(recipientFormAddress.zipCode)) {
        checkZipcode = true;
    }

    const checkBase = (checkCity && checkState && checkCountry && checkZipcode);
    if (!checkBase) {
        return resultSet;
    }

    if (
        trimAndLowerCase(qasFormatAddress.addressLine1) === trimAndLowerCase(recipientFormAddress.address1)
        && trimAndLowerCase(qasFormatAddress.addressLine2) === trimAndLowerCase(recipientFormAddress.address2)
    ) {
        resultSet.allowSave = true;
        return resultSet;
    } if (verfiytheAddress(qasFormatAddress.addressLine1, recipientFormAddress.address1)) {
        resultSet.allowSave = true;
        resultSet.recipientFormAddress.address1 = qasFormatAddress.addressLine1;
    }
    return resultSet;
}

function compareSuggestion(addressToCompare, addressList) {
    const {
        address1, address2, city, state, zipCode,
    } = addressToCompare;
    let lineAddress = `${trimAndLowerCase(address1)}`;
    if (trimAndLowerCase(address2)) lineAddress = `${lineAddress} ${trimAndLowerCase(address2)}`;
    if (trimAndLowerCase(city)) lineAddress = `${lineAddress} ${trimAndLowerCase(city)}`;
    if (trimAndLowerCase(state)) lineAddress = `${lineAddress} ${trimAndLowerCase(state)}`;
    if (trimAndLowerCase(zipCode)) {
        let zipCodeTransform = trimAndLowerCase(zipCode);
        if (/(-)/g.test(String(zipCodeTransform))) {
            zipCodeTransform = convertToArray(zipCodeTransform, '-')[0] || zipCodeTransform;
        }
        lineAddress = `${lineAddress} ${trimAndLowerCase(zipCodeTransform)}`;
    }
    const filteredAddress = addressList.filter((address) => {
        const isMatched = checkAddressMatchingPercentage(address, lineAddress);
        return isMatched;
    });

    return filteredAddress;
}

function queryAddressFormation(formData) {
    let searchText = '';
    if (trimStr(formData.address1) && !trimStr(formData.address2)) {
        searchText = `${trimStr(formData.address1)},`;
    } else {
        searchText = `${trimStr(formData.address1)}`;
    }
    if (trimStr(formData.address2)) searchText = `${searchText} ${trimStr(formData.address2)},`;
    if (trimStr(formData.city)) searchText = `${searchText} ${trimStr(formData.city)}`;
    if (trimStr(formData.state)) searchText = `${searchText} ${trimStr(formData.state)}`;
    if (trimStr(formData.zipCode)) searchText = `${searchText} ${trimStr(formData.zipCode.split('-')[0])}`;
    return searchText;
}

function queryAddressLineFormation(formData) {
    let searchText = '';
    if (trimStr(formData.address1) && !trimStr(formData.address2)) {
        searchText = `${trimStr(formData.address1)},`;
    } else {
        searchText = `${trimStr(formData.address1)}`;
    }
    if (trimStr(formData.address2)) searchText = `${searchText} ${trimStr(formData.address2)},`;
    return searchText;
}

/**
 * QAS Address Verification verify recipient address
 * returns {
 *      allowSave: true | false,
 *      addressVerifiedStatus: {
 *          dpvIndicator: 'Y' || 'N' || DPV value returned from QASAddress
 *          isAddressVerified: U - Unverified || V - Verified || O - Overwrite
 *      }
 * }
 */
function* workerValidateAddressQAS(action) {
    let allowSave = false;
    let qasLocationType = null;
    const addressVerifiedStatus = {
        dpvIndicator: 'N',
        isAddressVerified: 'U',
    };

    try {
        yield put(updateShowLoadingMessage());

        // Reset QAS State
        yield put(resetQasState());

        const recipientFormDetails = action.data.recipient;
        const searchText = yield call(queryAddressFormation, recipientFormDetails);
        const searchTextAddressLine = yield call(queryAddressLineFormation, recipientFormDetails);
        const countryCode = recipientFormDetails.country;
        const city = recipientFormDetails.city;

        const searchArgs = {
            data: {
                searchText, countryCode, city, searchTextAddressLine,
            },
        };
        const responseSearchQAS = yield call(workerQueryQasAddressSearch, searchArgs);

        if (responseSearchQAS.suggestions) {
            if (responseSearchQAS.suggestions?.length > 0) {
                const matchedAddress = yield call(compareSuggestion, recipientFormDetails, responseSearchQAS.suggestions);

                if (matchedAddress.length === 1) {
                    const [addressOne] = matchedAddress;
                    const formatParams = { data: { address: addressOne } };
                    const responseFormatQasAddress = yield call(workerGetFormatQAS, formatParams);

                    if (responseFormatQasAddress.data?.result) {
                        const formatAddress = responseFormatQasAddress.data.result;
                        const qasFormatAddress = yield call(alignQasFormatResponse, formatAddress);
                        const compareAddressFormatResult = yield call(compareAddressWithQASFormat, qasFormatAddress, recipientFormDetails);
                        allowSave = compareAddressFormatResult.allowSave || false;
                        if (allowSave) {
                            const { metadata: { dpv, address_classification } } = responseFormatQasAddress.data;
                            const dpvIndicator = (dpv && dpv.dpv_indicator) ? dpv.dpv_indicator : 'N';
                            addressVerifiedStatus.dpvIndicator = dpvIndicator;
                            addressVerifiedStatus.isAddressVerified = 'V';
                            qasLocationType = address_classification?.delivery_type;
                            yield put(flagOffShowValidatedQAS());
                        } else {
                            yield put(flagOnShowValidatedQAS());
                            yield put(flagOffShowLoadingMessage());
                        }
                    } else {
                        // failed to format
                        // Since QAS api failed - we allow recipient address to be submitted without verification
                        // dpvIndicator: 'N', isAddressVerified: 'U' - set to the address
                        allowSave = true;
                        // addressVerifiedStatus.isAddressVerified = 'E';
                        yield put(flagOnQasFailed());
                        mbpLogger.logError({
                            appName: process.env.npm_package_name,
                            module: 'mbp-checkout',
                            function: 'validateAddressQAS',
                            message: 'Failed to query qas address format',
                            error: responseFormatQasAddress.result,
                        });
                    }
                } else {
                    allowSave = false;
                    yield put(flagOnShowValidatedQAS());
                    yield put(flagOffShowLoadingMessage());
                }
            }
        } else {
            // No suggestions from QAS
            allowSave = false;
            yield put(flagOnShowValidatedQAS());
            yield put(flagOffShowLoadingMessage());

            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                function: 'validateAddressQAS',
                message: 'Failed to find addresses from QAS',
                error: responseSearchQAS,
            });
        }
    } catch (ex) {
        yield put(flagOffShowLoadingMessage());

        const cartId = yield call(getOrderId);
        // ----- we can find the issue in full story
        console.error('QAS validation - error response', ex.response);
        console.error('QAS validation - error detail', ex?.response?.data?.error?.detail);
        // -----

        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'validateAddressQAS',
            jsError: ex,
            message: `validateAddressQAS Failed - ${cartId}`,
        });
        allowSave = true;
        yield put(flagOnQasFailed());
    }
    return {
        allowSave,
        addressVerifiedStatus,
        qasLocationType,
    };
}

const watchers = [
    fork(watcherAddressQASGetFormat),
];

const workers = {
    workerValidateAddressQAS,
};

export {
    watchers,
    workers,
    workerGetFormatQAS,
};

export default {};
